/**
 * Shortcuts named in onboarding texts (W2-14 onboarding-wizard; DESIGN.md §5.14, §8.1): the tour, the Help
 * articles and the empty-state texts never write a key into a message. They name the COMMAND (`KeyRef`), and the key
 * shown is the one the active keymap binds to it now (preset, user overrides, Alt-free and macOS variants), drawn as
 * key caps (`Kbd`, labelled in the user's keyboard layout). A command without a binding shows no key: messages are
 * written so that the sentence still reads (ICU `select` on 'none', or an optional "(key)" in articles).
 *
 *  - `boundChord`: the chord a command is bound to (matching args; a preferred chord first, e.g. C over Enter for
 *    the curve picker), or null.
 *  - `keyParams` + `KeyText`: a message whose parameters are keys. Bound keys are passed as private-use markers that
 *    `KeyText` turns into `Kbd` elements after formatting; unbound ones as 'none'.
 *  - `keyLabelParam`: the plain label (toasts and other plain-text places), or 'none'.
 *  - `useKeyBindings`: the effective bindings, re-read (a new list) whenever the keymap changes (rebinding, preset
 *    switch), so texts and memoised searches follow at once.
 */

import { useEffect, useReducer, type ReactNode } from 'react';
import type { Chord, CommandId, KeyBinding, KeymapService } from '../../app/registry/types';
import type { Json } from '../../core/util/types';
import { Kbd } from '../../ui';

/** A command whose shortcut a text names. */
export interface KeyRef {
  readonly command: CommandId;
  /** Only bindings whose args contain these values (e.g. `{ workspace: 'publish' }` for F4). */
  readonly args?: Readonly<Record<string, string | number | boolean>>;
  /** Chords preferred when the command has several bindings (shown when bound, else the first binding). */
  readonly prefer?: readonly Chord[];
}

/** Value of an unbound key parameter (messages `select` on it). */
export const NO_KEY = 'none';

const OPEN = '';
const CLOSE = '';
const MARKER = /([A-Za-z0-9_]+)/g;

function argsMatch(args: Json | undefined, want: KeyRef['args']): boolean {
  if (want === undefined) return true;
  if (args === null || typeof args !== 'object' || Array.isArray(args)) return false;
  const have = args as { readonly [k: string]: Json };
  return Object.entries(want).every(([k, v]) => have[k] === v);
}

/** The chord the keymap binds to `ref` now, or null. */
export function boundChord(keymap: Pick<KeymapService, 'bindings'>, ref: KeyRef): Chord | null {
  let first: Chord | null = null;
  let preferred: Chord | null = null;
  let rank = Infinity;
  for (const b of keymap.bindings()) {
    if (b.command !== ref.command || b.remove === true || !argsMatch(b.args, ref.args)) continue;
    first ??= b.chord;
    const i = ref.prefer?.indexOf(b.chord) ?? -1;
    if (i >= 0 && i < rank) {
      rank = i;
      preferred = b.chord;
    }
  }
  return preferred ?? first;
}

/**
 * The chord as a key cap shows it inside a sentence: without the `hold:` prefix (the sentence says "hold"; a
 * command of kind 'hold' behaves as one on a plain chord too).
 */
export function displayChord(chord: Chord): Chord {
  return chord.replace(/^hold:/i, '');
}

/** Plain label of `ref`'s key (layout-aware, from the keymap), or NO_KEY. */
export function keyLabelParam(keymap: Pick<KeymapService, 'bindings' | 'label'>, ref: KeyRef): string {
  const chord = boundChord(keymap, ref);
  if (chord === null) return NO_KEY;
  try {
    return keymap.label(displayChord(chord));
  } catch {
    return displayChord(chord);
  }
}

/** Message parameters for keys: a marker per bound key (see `KeyText`), NO_KEY per unbound one. */
export function keyParams(
  keymap: Pick<KeymapService, 'bindings'>,
  refs: Readonly<Record<string, KeyRef>>,
): { readonly params: Readonly<Record<string, string>>; readonly chords: ReadonlyMap<string, Chord> } {
  const params: Record<string, string> = {};
  const chords = new Map<string, Chord>();
  for (const [name, ref] of Object.entries(refs)) {
    const chord = boundChord(keymap, ref);
    if (chord === null) params[name] = NO_KEY;
    else {
      params[name] = `${OPEN}${name}${CLOSE}`;
      chords.set(name, displayChord(chord));
    }
  }
  return { params, chords };
}

/** Splits formatted text at the key markers of `keyParams`: text runs and chords, in order. */
export function splitKeyText(
  text: string,
  chords: ReadonlyMap<string, Chord>,
): Array<{ readonly kind: 'text'; readonly text: string } | { readonly kind: 'key'; readonly chord: Chord }> {
  const out: Array<{ readonly kind: 'text'; readonly text: string } | { readonly kind: 'key'; readonly chord: Chord }> =
    [];
  let last = 0;
  for (const match of text.matchAll(MARKER)) {
    const at = match.index;
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) });
    const chord = chords.get(match[1] ?? '');
    if (chord !== undefined) out.push({ kind: 'key', chord });
    last = at + match[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

/** Formatted text with key caps where `keyParams` put markers. */
export function KeyText(props: { readonly text: string; readonly chords: ReadonlyMap<string, Chord> }): ReactNode {
  return splitKeyText(props.text, props.chords).map((part, i) =>
    part.kind === 'text' ? part.text : <Kbd key={`${String(i)}:${part.chord}`} chord={part.chord} />,
  );
}

/** Plain text of a formatted message with markers (screen-reader labels, search): keys as their labels. */
export function plainKeyText(
  text: string,
  chords: ReadonlyMap<string, Chord>,
  label: (chord: Chord) => string,
): string {
  return splitKeyText(text, chords)
    .map((part) => (part.kind === 'text' ? part.text : label(part.chord)))
    .join('');
}

type KeymapSource = Pick<KeymapService, 'bindings' | 'subscribe'>;

interface BindingsState {
  readonly keymap: KeymapSource;
  readonly bindings: readonly KeyBinding[];
}

const readBindings = (keymap: KeymapSource): BindingsState => ({ keymap, bindings: [...keymap.bindings()] });

/** The keymap's effective bindings: a new list whenever the keymap changes (bindings, preset, overrides). */
export function useKeyBindings(keymap: KeymapSource): readonly KeyBinding[] {
  const [state, refresh] = useReducer((_: BindingsState, km: KeymapSource) => readBindings(km), keymap, readBindings);
  useEffect(
    () =>
      keymap.subscribe(() => {
        refresh(keymap);
      }),
    [keymap],
  );
  return state.keymap === keymap ? state.bindings : keymap.bindings();
}

/** A `bindings` source over a list (for `boundChord` / `keyParams` with `useKeyBindings`). */
export function bindingsOf(list: readonly KeyBinding[]): Pick<KeymapService, 'bindings'> {
  return { bindings: () => list };
}
